/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import { FaRegCalendarAlt, FaRegHeart } from "react-icons/fa";
import { BiLoaderAlt } from "react-icons/bi";
import { IoMdClose } from "react-icons/io";
import { useRecoilState } from "recoil";
import { getFreeFavoriteTime, updateUser } from "../api/User";
import { ScheduleDateAtom } from "../atoms/ScheduleDateAtom";
import { UserAtom } from "../atoms/UserAtom";
import { IScheduleIntervalOptions, IUserIRoute } from "../generated/Api";
import useClickOutside from "../hook/useClickOutside";
import { notSelect } from "../styles/general";
import "../styles/react-calendar-custom.module.css";
import calendarStyles from "../styles/react-calendar-custom.module.css";
import EmptyFavoriteTimes from "./EmptyFavoriteTimes";
import StyledButton from "./StyledButton";
import { StyledRadio } from "./StyledRadio";
import StyledTimePicker from "./StyledTimePicker";

const Container = styled.div`
  width: 100%;
  height: 40px;
  border: 1px solid #dddddd;
  border-radius: 4px;
  outline: none;
  font-size: 16px;
  padding: 5px;
  color: #383838;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #ffffff;
  margin-bottom: 5px;
  ${notSelect}
`;

const CalendarContainer = styled.div<{ hideCalendar: boolean }>`
  display: ${(props) => (props.hideCalendar ? "none" : "block")};
  border: 1px solid #dddddd;
  border-radius: 4px;
  background-color: white;
`;

const TimePickerContainer = styled.div`
  background-color: #ffffff;
  border: 1px solid #dddddd;
  border-radius: 4px;
  height: 320px !important;
`;

const PickersContainer = styled.div`
  position: relative;
  /* max-height: 325px; */ //??????
  z-index: 5;
  ${notSelect}
`;

const TimeInputContainer = styled.div<{ dateLabel: string }>`
  display: flex;
  color: ${(props) =>
    props.dateLabel === "(MM/DD/YY" ? "#979797" : "#2f2f2f"};
  cursor: pointer;
  padding-left: 4px;
  font: normal normal 400 14px "Source Sans Pro";

  /* > div:first-of-type {
    margin-right: 7px;
  } */
`;

const CalendarIcon = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;

  padding: 4px;
  cursor: pointer;
  .spinLoadIcon {
    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
    animation: spin 1.2s linear infinite;
  }
`;

const Select = styled.div`
  cursor: pointer;
  height: 22px;
  width: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;

  :hover {
    background-color: #22222217;
  }
`;

const CalendarButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const PreferencesOptions = styled.div`
  background-color: #ffffff;
  display: flex;
  font-weight: 400;
  flex-direction: column;
  width: 100%;
  position: absolute;
  top: 50px;
  left: 0px;
  z-index: 2;
  padding: 5px 10px;
  -webkit-box-shadow: 0px 0px 15px -1px #000000;
  box-shadow: 0px 0px 15px -1px #cccccc;
  opacity: 1 !important;
  cursor: default;
`;

const Flex = styled.div`
  display: flex;
  flex-direction: row;
  font-size: 15px;
  margin: 7px 0;
`;

const FlexTitle = styled.div`
  color: #888888;
  font-weight: 400;
  display: flex;
  flex-direction: row;
  font-size: 14px;
  margin: 5px 0;
`;

const FavoriteTitle = styled.div`
  font-weight: 600;
  color: #333333;
  margin-bottom: 10px;
`;

interface PreferencesContainterProps {
  intervals: 5 | 15;
  setIntervals: (value: 5 | 15) => any;
  close: () => any;
}

const PreferencesContainter = ({
  intervals,
  setIntervals,
  close,
}: PreferencesContainterProps) => {
  return (
    <PreferencesOptions>
      <FlexTitle>INTERVALS</FlexTitle>
      <Flex
        css={css`
          align-items: center;
          grid-gap: 8px;
          cursor: pointer;
          .css-1a7nm9t-StyledRadio {
            min-width: 16px;
            min-height: 16px;
            max-width: 16px;
            max-height: 16px;
          }
          .css-i5nnow-StyledRadio {
            min-width: 4px;
            min-height: 4px;
            max-width: 4px;
            max-height: 4px;
          }
        `}
        onClick={() => {
          setIntervals(5);
        }}
      >
        <StyledRadio check={intervals === 5} />
        5-minute intervals
      </Flex>
      <Flex
        css={css`
          align-items: center;
          grid-gap: 8px;
          cursor: pointer;
          .css-1a7nm9t-StyledRadio {
            min-width: 16px;
            min-height: 16px;
            max-width: 16px;
            max-height: 16px;
          }
          .css-i5nnow-StyledRadio {
            min-width: 4px;
            min-height: 4px;
            max-width: 4px;
            max-height: 4px;
          }
        `}
        onClick={() => {
          setIntervals(15);
        }}
      >
        <StyledRadio check={intervals === 15} />
        15-minute intervals
      </Flex>
    </PreferencesOptions>
  );
};

function convertFavoriteTimes(times: string[]) {
  const availableTimes = times
    .map((t) => {
      const hour = dayjs(t).get("hours");
      const minute = dayjs(t).get("minute");

      const newDate = dayjs().set("hours", hour).set("minutes", minute);

      const isBeforeNow = newDate.isBefore(dayjs());

      if (isBeforeNow) {
        return newDate.add(1, "day").toISOString();
      } else {
        return newDate.toISOString();
      }
    })
    .slice()
    .sort((a, b) => dayjs(a).diff(b));

  return availableTimes;
}

interface FavoriteTimerCardProps {
  time: string;
  handleDelete: (time: string) => void;
}

interface Props {
  showTimePicker?: boolean;
  showDatePicker?: boolean;
  date: Date | undefined;
  input?: boolean;
  onDateSelect: (date: Date) => void;
  onTimeSelect: (date: Date) => void;
  calendarKey?: string;
  setCalendarKey?: (value: string) => any;
  disabled?: boolean;
  isCalendarOpen?: (date: boolean) => void;
  onReset?: () => void;
  hideFavorites?: boolean;
  minDate?: Date;
  hideTodayButton?: boolean;
  startHideCalendar?: boolean;
}

export default function DateTimePicker(props: Props): JSX.Element {
  const {
    showTimePicker,
    showDatePicker,
    onDateSelect,
    onTimeSelect,
    calendarKey,
    setCalendarKey,
    date,
    input,
    disabled,
    onReset,
    isCalendarOpen,
    hideFavorites,
    minDate,
    hideTodayButton,
    startHideCalendar,
  } = props;

  const [isTimePickerActive, setTimePickerActivity] = useState(false);
  const [dateLabel, setDateLabel] = useState<string>(
    showTimePicker ? "(MM/DD/YY" : "(MM/DD/YY)"
  );
  const [timeLabel, setTimeLabel] = useState("HH:MM UTC)");
  const [hideCalendar, setHideCalendar] = useState<boolean>(
    startHideCalendar ? startHideCalendar : false
  );
  const [showPreferences, setShowPreferences] = useState<boolean>(false);
  const [showFavorites, setShowFavorites] = useState<boolean>(false);
  const [intervals, setIntervals] = useState<5 | 15>(5);
  const [step, setStep] = useState<"hour" | "minute">("hour");
  const [user, setUser] = useRecoilState(UserAtom);
  const [scheduleDate, setScheduleDate] = useRecoilState(ScheduleDateAtom);
  const [indexFavoriteTime, setIndexFavoriteTime] = useState(0);
  const [isHoverOnInput, setIsHoverOnInput] = useState(false);
  const [isLoadingFavoriteTime, setIsLoadingFavoriteTime] = useState(false);
  const [isSelectFavoriteTimeIntention, setIsSelectFavoriteTimeIntention] =
    useState(false);
  useEffect(() => {
    if (!user?.options?.favoriteScheduleTimes) return;

    if (scheduleDate) return;

    const availableTimes = convertFavoriteTimes(
      user?.options?.favoriteScheduleTimes
    );

    let foundIndex = false;

    availableTimes.forEach((t, index) => {
      if (dayjs(t).isAfter(dayjs()) && !foundIndex) {
        setIndexFavoriteTime(index);
        foundIndex = true;
      }
    });
  }, [user?.options?.favoriteScheduleTimes, scheduleDate]);

  const buttonRef = useClickOutside(() => {
    setShowPreferences(false);
  });

  const wrapperRef = useClickOutside(() => {
    if (date) {
      setHideCalendar(true);
      setTimePickerActivity(false);
      setShowFavorites(false);
    }
  });

  useEffect(() => {
    if (isCalendarOpen) isCalendarOpen(!hideCalendar || isTimePickerActive);
  }, [hideCalendar, isTimePickerActive, isCalendarOpen]);

  useEffect(() => {
    if (date) {
      setDateLabel(dayjs(date).format("l"));
      if (date.getMinutes() !== 21)
        setTimeLabel(
          ` @ ${dayjs(date).format("LT").toLowerCase()} ${dayjs(date).format(
            "(z)"
          )}`
        );
      else setTimeLabel(" @ --:--");
    } else {
      setDateLabel(showTimePicker ? "(MM/DD/YY" : "(MM/DD/YY)");
      setTimeLabel(" @ HH:MM (UTC))");
      setHideCalendar(onReset ? true : false);
    }
  }, [date]);

  useEffect(() => {
    if (!user) return;
    switch (user.options.scheduleInterval) {
      case IScheduleIntervalOptions.FIVE_MINUTES:
        setIntervals(5);
        break;
      case IScheduleIntervalOptions.FIFTEEN_MINUTES:
        setIntervals(15);
        break;
      default:
    }
  }, [user]);

  async function handleChangeInterval(interval: IScheduleIntervalOptions) {
    const userInfoToUpdateCopy: IUserIRoute = {
      ...user,
      options: {
        ...user.options,
        scheduleInterval: interval,
      },
    };

    const isSuccess = await updateUser(userInfoToUpdateCopy);

    if (isSuccess) {
      setUser(userInfoToUpdateCopy as any);
    }
  }

  function handleShowCalendar() {
    if (disabled) return;
    setHideCalendar(!hideCalendar);

    setTimePickerActivity(false);
    setShowFavorites(false);
  }

  function handleShowTimePicker() {
    setTimePickerActivity(!isTimePickerActive);

    setHideCalendar(true);
    setShowFavorites(false);
  }

  function handleShowFavoriteTime() {
    setShowFavorites(!showFavorites);

    setHideCalendar(true);
    setTimePickerActivity(false);
  }

  function goToToday() {
    const currentDate = new Date();
    // if (date) {
    //   currentDate.setTime(date.getTime());
    // }
    currentDate.setMinutes(21);
    currentDate.setDate(new Date().getDate());
    currentDate.setMonth(new Date().getMonth());
    currentDate.setFullYear(new Date().getFullYear());
    onDateSelect(currentDate);

    setTimeout(() => {
      handleShowCalendar();
      handleShowTimePicker();
    }, 60);

    // if (isTimePickerActive && showTimePicker) {
    //   handleShowCalendar();
    // }
    if (setCalendarKey) setCalendarKey(new Date().getTime() + "");
  }

  useEffect(() => {
    if (!isTimePickerActive || !showTimePicker) setStep("hour");
  }, [isTimePickerActive, showTimePicker]);

  function isFavoriteTimeBeforeNow(time: string) {
    const hour = dayjs(time).get("hours");
    const minute = dayjs(time).get("minute");

    const isBeforeNow = dayjs()
      .set("hours", hour)
      .set("minutes", minute)
      .isBefore(dayjs());

    return isBeforeNow;
  }

  async function selectFavoriteTime() {
    console.log("select", scheduleDate);
    const { favoriteTime } = await getFreeFavoriteTime(
      isSelectFavoriteTimeIntention ? scheduleDate?.toISOString() : null
    );
    setIsLoadingFavoriteTime(false);
    setIsSelectFavoriteTimeIntention(true);

    setScheduleDate(new Date(favoriteTime));
  }

  function isSameHourAndMinute(timeOne: Date, timeTwo: Date) {
    const hourOne = dayjs(timeOne).get("hour");
    const minuteOne = dayjs(timeOne).get("minute");

    const hourTwo = dayjs(timeTwo).get("hour");
    const minuteTwo = dayjs(timeTwo).get("minute");

    if (hourOne === hourTwo && minuteOne === minuteTwo) return true;
    return false;
  }

  //Reset index to get favorite times
  useEffect(() => {
    //When disable favorite time, close favorite time cards
    if (!user?.options.enableFavoriteTimes) {
      setShowFavorites(false);

      return;
    }

    setIndexFavoriteTime(0);

    if (user?.options.favoriteScheduleTimes.length === 0) return;

    const availableTimes = user?.options.favoriteScheduleTimes;

    //It can't reset always to the index zero, because sometimes the time selected is the index zero, so we move to next index
    if (availableTimes?.length > 1) {
      let index;
      const timesFound = availableTimes.filter((time, i) => {
        if (isSameHourAndMinute(dayjs(time).toDate(), date)) {
          index = i;
          return true;
        } else {
          return false;
        }
      });

      if (index === 0) {
        setIndexFavoriteTime(1);
      }
    }
  }, [user?.options.enableFavoriteTimes, user?.options.favoriteScheduleTimes]);

  return (
    <div
      ref={wrapperRef}
      css={css`
        opacity: ${disabled ? 0.5 : 1};
      `}
    >
      {input && (
        <Container
          onMouseEnter={() => {
            setIsHoverOnInput(true);
          }}
          onMouseLeave={() => {
            setIsHoverOnInput(false);
          }}
        >
          <TimeInputContainer dateLabel={dateLabel}>
            <div
              onClick={(e) => {
                e.stopPropagation();
                handleShowCalendar();
              }}
            >
              {dateLabel}
            </div>
            {showTimePicker && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  if (dateLabel !== "(MM/DD/YY") handleShowTimePicker();
                  else handleShowCalendar();
                }}
              >
                {timeLabel}
              </div>
            )}
          </TimeInputContainer>

          {onReset && date ? (
            <Flex>
              {isHoverOnInput && (
                <Select
                  css={css`
                    margin-right: 2px;
                  `}
                  onClick={(e) => {
                    e.stopPropagation();
                    onReset();
                  }}
                >
                  <IoMdClose color="#979797" size={16} />
                </Select>
              )}

              <CalendarIcon
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowCalendar();
                }}
              >
                <FaRegCalendarAlt width="16px" height="16px" color="#979797" />
              </CalendarIcon>
            </Flex>
          ) : (
            <Flex>
              {user?.options.enableFavoriteTimes && !hideFavorites && (
                <CalendarIcon
                  css={css`
                    margin-right: 2px;
                  `}
                  disabled={isLoadingFavoriteTime}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (user.options.favoriteScheduleTimes.length > 0) {
                      setIsLoadingFavoriteTime(true);
                      await selectFavoriteTime();
                    } else handleShowFavoriteTime();
                  }}
                >
                  {isLoadingFavoriteTime ? (
                    <BiLoaderAlt
                      className="spinLoadIcon"
                      width="16px"
                      height="16px"
                      color="#979797"
                    />
                  ) : (
                    <FaRegHeart width="16px" height="16px" color="#979797" />
                  )}
                </CalendarIcon>
              )}
              <CalendarIcon
                onClick={(e) => {
                  e.stopPropagation();
                  handleShowCalendar();
                }}
              >
                <FaRegCalendarAlt width="16px" height="16px" color="#979797" />
              </CalendarIcon>
            </Flex>
          )}
        </Container>
      )}
      <PickersContainer className="calendar">
        {showDatePicker && (
          <CalendarContainer
            onClick={() => {
              console.log(isSelectFavoriteTimeIntention);
              setIsSelectFavoriteTimeIntention(false);
            }}
            hideCalendar={hideCalendar}
          >
            <Calendar
              minDate={minDate ? minDate : new Date()}
              onChange={(vle: Date) => {
                if (timeLabel === "HH:MM)" || timeLabel === " @ HH:MM)") {
                  vle.setMinutes(21);
                }

                onDateSelect(vle as Date);
                setTimeout(() => {
                  handleShowCalendar();
                  handleShowTimePicker();
                }, 60);
              }}
              formatMonth={(locale, date) => dayjs(date).format("MMM")}
              value={date}
              locale="us"
              className={calendarStyles["no-boder-bottom"]}
              key={calendarKey}
              calendarType={"US"}
              allowPartialRange={false}
            />
            {!hideTodayButton && (
              <CalendarButtons>
                <StyledButton
                  onClick={() => {
                    goToToday();
                  }}
                  inverted
                  css={css`
                    width: 100%;
                    margin: 0 10px 10px;
                    :hover {
                      background-color: #efefef;
                    }
                  `}
                >
                  Today
                </StyledButton>
              </CalendarButtons>
            )}
          </CalendarContainer>
        )}
        {isTimePickerActive && showTimePicker && !showFavorites && (
          <TimePickerContainer>
            <StyledTimePicker
              step={step}
              setStep={setStep}
              goBack={() => {
                handleShowCalendar();
              }}
              date={date}
              minuteInterval={intervals}
              onSelect={({ hour, minute }) => {
                handleShowTimePicker();
                const selectedDateTime = new Date();

                if (date) {
                  const year = dayjs(date).get("year");
                  const month = dayjs(date).get("month");
                  const day = dayjs(date).get("date");

                  selectedDateTime.setDate(+day);
                  selectedDateTime.setMonth(+month);
                  selectedDateTime.setFullYear(+year);
                }

                selectedDateTime.setHours(hour as number);
                selectedDateTime.setMinutes(minute as number);
                selectedDateTime.setSeconds(0);

                onTimeSelect(selectedDateTime);
              }}
            />
            <CalendarButtons>
              {step === "minute" ? (
                <div ref={buttonRef} style={{ width: "100%", display: "flex" }}>
                  <StyledButton
                    inverted
                    onClick={() => {
                      setShowPreferences(!showPreferences);
                    }}
                    css={css`
                      width: 100%;
                      margin: 0px 10px 10px;
                      position: relative;
                      opacity: 1 !important;
                      background-color: ${showPreferences && "#efefef"};
                      :hover {
                        background-color: #efefef;
                      }
                    `}
                  >
                    Intervals
                    {showPreferences && (
                      <PreferencesContainter
                        intervals={intervals}
                        setIntervals={(vle) => {
                          setIntervals(vle);

                          const interval =
                            vle === 5
                              ? IScheduleIntervalOptions.FIVE_MINUTES
                              : IScheduleIntervalOptions.FIFTEEN_MINUTES;

                          handleChangeInterval(interval);
                        }}
                        close={() => setShowPreferences(false)}
                      />
                    )}
                  </StyledButton>
                </div>
              ) : (
                <StyledButton
                  onClick={() => {
                    goToToday();
                  }}
                  inverted
                  css={css`
                    width: 100%;
                    margin: 0 10px 10px;
                    :hover {
                      background-color: #efefef;
                    }
                  `}
                >
                  Today
                </StyledButton>
              )}
            </CalendarButtons>
          </TimePickerContainer>
        )}
        {showTimePicker &&
          showFavorites &&
          user?.options.favoriteScheduleTimes.length === 0 && (
            <EmptyFavoriteTimes
              handleShowFavoriteTime={handleShowFavoriteTime}
            />
          )}
      </PickersContainer>
    </div>
  );
}
